import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import copy from 'copy-to-clipboard';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';
import CloseIcon from 'svg/CloseIcon';
import { Button } from 'components/Button';
import Modal from 'components/modals/Modal';

interface Props {
  isOpen: boolean;
  closeModal: (value: boolean) => void;
  shareUrl: string;
  title: string;
  buttonText?: string;
}

const QR_CODE_DISPLAY_SIZE = 128;
const QR_CODE_DOWNLOAD_SIZE = 2048;

export default function ModalShare({
  isOpen,
  closeModal,
  shareUrl,
  title,
  buttonText = 'Copy link',
}: Props) {
  const qrCodeRef = React.useRef<HTMLDivElement>(null);

  const downloadQRCode = () => {
    if (!qrCodeRef.current) return;

    const svg = qrCodeRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = QR_CODE_DOWNLOAD_SIZE;
    canvas.height = QR_CODE_DOWNLOAD_SIZE;

    img.onload = () => {
      if (!ctx) return;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = 'qr-code.png';
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <Modal isOpen={isOpen} handleClose={() => closeModal(false)}>
      <div className="p-6">
        <div className="flex justify-between">
          <h3 className="text-xl font-bold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {title}
          </h3>
          <button
            className="p-0.5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            type="button"
            onClick={() => closeModal(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <div>
          <div className="flex justify-center py-8">
            <QRCode value={shareUrl} size={QR_CODE_DISPLAY_SIZE} />
            <div className="hidden" ref={qrCodeRef}>
              <QRCode value={shareUrl} size={QR_CODE_DOWNLOAD_SIZE} />
            </div>
          </div>
          <div className="flex w-full flex-col space-y-4">
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                try {
                  copy(shareUrl);
                  toast.success('Link copied');
                } catch (error) {
                  Sentry.captureException(error);
                  toast.error('Could not copy');
                }
              }}
            >
              {buttonText}
            </Button>
            <Button variant="secondary" size="lg" onClick={downloadQRCode}>
              Download QR Code
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
